import { HgSupportedLanguage } from '@smg-real-estate/domus';

import type { Insertion } from '@/types';

// UTM context enums for Flatfox links
export enum FlatfoxUtmContext {
  LEAD_MANAGEMENT = 'ubm-lead-mgmt',
  AUTO_REPLY = 'ubm-auto-reply',
  APPLICATION_FORM = 'ubm-appl-form',
  VIEWING_APPOINTMENTS = 'ubm-viewing-appts',
  VIEW_LEADS = 'ubm-view-leads',
}

// UTM generation helper
export const applyFlatfoxUtmParams = (
  originalUrl: URL,
  lang: HgSupportedLanguage = HgSupportedLanguage.EN,
  flatfoxUtmContext?: FlatfoxUtmContext,
): void => {
  if (!flatfoxUtmContext) {
    return;
  }

  originalUrl.searchParams.set('utm_medium', 'referral');
  originalUrl.searchParams.set('utm_source', 'smg');
  originalUrl.searchParams.set(
    'utm_campaign',
    `(CG_pla)(N_smgbc-ff-adpt)(C_own-ref)(L_ch-${lang.toLowerCase()})(G_msg)(U_cs)(D_web)(BR_ff)(E_${flatfoxUtmContext})(X_pr)`,
  );
};

// base functions, should return URL object ==================
export const getFlatfoxBaseUrl = (): URL => {
  const url = new URL(import.meta.env.VUE_FLATFOX_URL);
  url.searchParams.set('login_hint', 'smg');
  return url;
};

export const getFlatfoxCustomerUrl = (customerId: string, lang = HgSupportedLanguage.EN): URL => {
  const url = getFlatfoxBaseUrl();
  url.pathname = [HgSupportedLanguage.EN, HgSupportedLanguage.FR, HgSupportedLanguage.IT].includes(
    lang,
  )
    ? `${lang}/my/flat/customer/${customerId}/`
    : `de/meine/wohnung/customer/${customerId}/`;

  return url;
};

export const flatfoxInsertionRefUrl = (
  customerId: string,
  insertion: Insertion,
  lang = HgSupportedLanguage.EN,
): URL => {
  const customerUrl = getFlatfoxCustomerUrl(customerId, lang);

  // at least one of these is required and will be present; although all are optional and need safeguarding
  // Flatfox URLs use refProperty.refHouse.refObject; missing levels are empty strings (e.g. "..objectId" or "PC17..").
  const refObject = insertion.listing.externalIds?.refObject ?? '';
  const refHouse = insertion.listing.externalIds?.refHouse ?? '';
  const refProperty = insertion.listing.externalIds?.refProperty ?? '';

  customerUrl.pathname += `ref/${refProperty}.${refHouse}.${refObject}/`;

  return customerUrl;
};
// ==================

// exported URL-specific functions, should always return STRING, not URL object ==================
export const flatfoxLeadsUrl = (customerId: string, lang = HgSupportedLanguage.EN): string => {
  const url = getFlatfoxCustomerUrl(customerId, lang);
  applyFlatfoxUtmParams(url, lang, FlatfoxUtmContext.LEAD_MANAGEMENT);
  url.searchParams.set('adv', 'all');
  return url.toString();
};

export const flatfoxInsertionThreadsUrl = (
  customerId: string,
  insertion: Insertion,
  lang = HgSupportedLanguage.EN,
): string => {
  const url = flatfoxInsertionRefUrl(customerId, insertion, lang);
  applyFlatfoxUtmParams(url, lang, FlatfoxUtmContext.VIEW_LEADS);
  url.pathname += 'thread/';
  return url.toString();
};

export enum FlatfoxInsertionPageModals {
  DOWNLOAD_FLYER = 'download-flyer',
  VIEWING_SCHEDULE = 'viewing-schedule',
  WELCOME_MESSAGE = 'welcome-message',
}

const utmContextForModalMap: Record<FlatfoxInsertionPageModals, FlatfoxUtmContext> = {
  [FlatfoxInsertionPageModals.DOWNLOAD_FLYER]: FlatfoxUtmContext.APPLICATION_FORM,
  [FlatfoxInsertionPageModals.VIEWING_SCHEDULE]: FlatfoxUtmContext.VIEWING_APPOINTMENTS,
  [FlatfoxInsertionPageModals.WELCOME_MESSAGE]: FlatfoxUtmContext.AUTO_REPLY,
};

export const flatfoxInsertionUrlWithModal = (
  customerId: string,
  insertion: Insertion,
  modal: FlatfoxInsertionPageModals,
  lang = HgSupportedLanguage.EN,
): string => {
  const url = flatfoxInsertionRefUrl(customerId, insertion, lang);
  applyFlatfoxUtmParams(url, lang, utmContextForModalMap[modal]);
  url.pathname += `${modal}/`;
  return url.toString();
};
